import { useState, useContext, useEffect } from "react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signInWithRedirect,
  getRedirectResult,
  setPersistence,
  browserLocalPersistence,
  OAuthProvider,
  signOut,
} from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { auth, firestore } from "firebaseConfig";
import { AuthContext } from "App";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import GoogleIcon from "@mui/icons-material/Google";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import logoBranca from "assets/images/logos/LogoSemFundo.png";
import { isMobile } from "react-device-detect";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { useLocation } from "react-router-dom";
import iconeSemFundo from "assets/images/logos/IconeSemFundo.png";

function Login() {
  const [rememberMe, setRememberMe] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/authentication/sign-in") {
      setShowModal(false);
      setModalMessage("");
    }
  }, [location]);

  const [modalMessage, setModalMessage] = useState("");
  useEffect(() => {
    if (showModal) {
      console.log("Modal aberto. Mensagem:", modalMessage);
    }
  }, [showModal, modalMessage]);

  useEffect(() => {
    setShowModal(false);
    setModalMessage("");
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState("");
  const navigate = useNavigate();
  const googleProvider = new GoogleAuthProvider();

  const { login } = useContext(AuthContext);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (user) {
            console.log("Usuário autenticado automaticamente:", user);

            const tokenResult = await user.getIdTokenResult();
            const role = tokenResult.claims.role || "cliente";

            login(user.email, role);

            setShowLogo(true);
            setTimeout(() => {
              const destination =
                role === "admin" ? "/dashboard-admin" : role === "consultor" ? "/dashboard-consultor" : "/dashboard";

              navigate(destination, { replace: true });
            }, 1000);
          } else {
            console.log("Nenhum usuário autenticado.");
            setIsLoading(false);
          }
        });

        return () => unsubscribe();
      } catch (error) {
        console.error("Erro ao configurar persistência:", error.message);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [login, navigate]);

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handleEmailSignIn = async () => {
    try {
      setShowLogo(true);

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userRef = doc(firestore, "usuarios", user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const role = userDoc.data().role || "cliente";
        login(email, role);

        const destination =
          role === "admin" ? "/dashboard-admin" : role === "consultor" ? "/dashboard-consultor" : "/dashboard";

        setTimeout(() => {
          navigate(destination, { replace: true });
          setShowLogo(false);
        }, 1000);
      } else {
        setShowLogo(false);
        setModalMessage("Conta não encontrada. Crie uma nova conta.");
        setShowModal(true);
      }
    } catch (error) {
      setShowLogo(false);
      setAuthError("Usuário ou senha inválidos.");
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setShowLogo(true);

      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      if (user) {
        const userRef = doc(firestore, "usuarios", user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const role = userDoc.data().role || "cliente";
          login(user.email, role);

          const destination =
            role === "admin" ? "/dashboard-admin" : role === "consultor" ? "/dashboard-consultor" : "/dashboard";

          setTimeout(() => {
            navigate(destination, { replace: true });
            setShowLogo(false);
          }, 1000);
        } else {
          await signOut(auth);
          setShowLogo(false);
          setModalMessage("Conta não encontrada. Crie uma nova conta.");
          setShowModal(true);
        }
      }
    } catch (error) {
      console.error("Erro ao autenticar com Google:", error.message);
      setShowLogo(false);
      setModalMessage("Erro ao autenticar com Google. Tente novamente.");
      setShowModal(true);
    }
  };

  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          console.log("Resultado do Redirect obtido:", result.user);

          const userRef = doc(firestore, "usuarios", result.user.uid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const role = userDoc.data().role || "cliente";
            console.log("Usuário encontrado no Firestore. Papel:", role);

            login(result.user.email, role);

            const destination =
              role === "admin" ? "/dashboard-admin" : role === "consultor" ? "/dashboard-consultor" : "/dashboard";

            console.log("Redirecionando para:", destination);
            navigate(destination, { replace: true });
          } else {
            console.log("Conta não encontrada no Firestore.");
            setModalMessage("Conta não encontrada. Crie uma nova conta.");
            setShowModal(true);
            setTimeout(() => navigate("/authentication/sign-up"), 2000);
          }
        }
      } catch (error) {
        console.error("Erro ao processar o resultado do Redirect:", error.message);
      }
    };

    handleRedirectResult();
  }, [navigate, login]);

  useEffect(() => {
    setShowPopup(false);
    setAuthError("");
  }, []);

  useEffect(() => {
    let lastTouchDistance = 0;
    const content = document.getElementById("content-wrapper");

    const handleTouchStart = (event) => {
      if (event.touches.length === 2) {
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        lastTouchDistance = Math.hypot(touch2.pageX - touch1.pageX, touch2.pageY - touch1.pageY);
      }
    };

    const handleTouchMove = (event) => {
      if (event.touches.length === 2) {
        event.preventDefault();
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const currentDistance = Math.hypot(touch2.pageX - touch1.pageX, touch2.pageY - touch1.pageY);

        const scale = Math.min(1.5, Math.max(1, currentDistance / lastTouchDistance));
        content.style.transform = `scale(${scale})`;
        content.style.transformOrigin = "center";
      }
    };

    const handleTouchEnd = () => {
      content.style.transition = "transform 0.3s ease";
      content.style.transform = "scale(1)";
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  const [showLogo, setShowLogo] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLogo(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (showLogo || isLoading) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100vw"
        style={{
          backgroundColor: "#FFFFFF",
        }}
      >
        <img
          src={iconeSemFundo}
          alt="Carregando..."
          style={{
            width: "150px",
            height: "auto",
          }}
        />
      </MDBox>
    );
  }

  return (
    <MDBox
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        overflow: "hidden",
        backgroundColor: "#F7F9FC",
      }}
    >
      {/* Seção Esquerda - Decorativa (com Logomarca) */}
      <MDBox
        sx={{
          display: { xs: "none", md: "flex" },
          width: { md: "50%" },
          background: "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Conteúdo */}
        <MDBox sx={{ position: "relative", zIndex: 10, maxWidth: "500px", p: 6, color: "white" }}>
          {/* Logomarca */}
          <MDBox textAlign="center" mb={2}>
            {" "}
            {/* Reduzido de mb={4} para mb={2} */}
            <Link to="/">
              <img
                src={logoBranca}
                alt="Logomarca"
                width="200" // Aumentado de 180px para 200px
                height="200"
                style={{ transition: "transform 0.3s ease", "&:hover": { transform: "scale(1.05)" } }}
              />
            </Link>
          </MDBox>

          <MDTypography
            variant="h2"
            fontWeight="bold"
            mb={4}
            sx={{
              fontFamily: "'Roboto Slab', serif",
              letterSpacing: "1px",
              background: "linear-gradient(90deg, #FFFFFF 0%, #E2E8F0 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            A Liberdade Financeira Começa Aqui
          </MDTypography>
          <MDTypography
            variant="body1"
            mb={6}
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: "1.1rem",
              lineHeight: "1.6",
            }}
          >
            Assuma o controle das suas finanças com nossas ferramentas abrangentes e insights personalizados para o seu
            sucesso financeiro.
          </MDTypography>

          {/* Lista de Recursos */}
          <MDBox component="ul">
            {[
              "Painel Personalizado",
              "Gestão Inteligente de Orçamento",
              "Acompanhamento de Metas Financeiras",
              "Relatórios Financeiros Detalhados",
            ].map((feature, index) => (
              <MDBox key={index} component="li" display="flex" alignItems="center" mb={3}>
                <MDBox
                  sx={{
                    width: "10px",
                    height: "10px",
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                    borderRadius: "50%",
                    mr: 3,
                  }}
                />
                <MDTypography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "1rem",
                  }}
                >
                  {feature}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>
      </MDBox>

      {/* Seção Direita - Formulário de Login */}
      <MDBox
        sx={{
          width: { xs: "100%", md: "50%" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 4, md: 8 },
        }}
      >
        <MDBox sx={{ width: "100%", maxWidth: "450px" }}>
          {/* Título e Subtítulo */}
          <MDBox textAlign="center" mb={8}>
            <MDTypography
              variant="h3"
              fontWeight="bold"
              color="dark"
              mb={2}
              sx={{
                fontFamily: "'Roboto Slab', serif",
                letterSpacing: "0.5px",
                background: "linear-gradient(90deg, #1E3A8A 0%, #6D28D9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Bem-vindo de Volta
            </MDTypography>
            <MDTypography variant="body1" color="text.secondary" sx={{ fontSize: "1.1rem", opacity: 0.8 }}>
              Entre para continuar na sua conta
            </MDTypography>
          </MDBox>

          {/* Formulário */}
          <Card
            sx={{
              p: 6,
              borderRadius: "20px",
              boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
            }}
          >
            <MDBox
              component="form"
              role="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleEmailSignIn();
              }}
            >
              <MDBox mb={4}>
                <MDTypography
                  variant="body2"
                  fontWeight="medium"
                  mb={1}
                  display="block"
                  sx={{ color: "#1E3A8A", fontSize: "0.9rem" }}
                >
                  Email
                </MDTypography>
                <MDInput
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "12px",
                      backgroundColor: "#F8FAFC",
                      height: "48px",
                      border: "1px solid #E2E8F0",
                      transition: "all 0.3s ease",
                      "&:hover": { borderColor: "#6D28D9" },
                    },
                    "& input": { color: "#1E3A8A", fontSize: "1rem" },
                  }}
                />
              </MDBox>

              <MDBox mb={4}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography
                    variant="body2"
                    fontWeight="medium"
                    mb={1}
                    sx={{ color: "#1E3A8A", fontSize: "0.9rem" }}
                  >
                    Senha
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    component={Link}
                    to="/authentication/reset-password"
                    sx={{
                      color: "#6D28D9",
                      cursor: "pointer",
                      textDecoration: "none",
                      fontSize: "0.85rem",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Esqueceu a senha?
                  </MDTypography>
                </MDBox>
                <MDInput
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "12px",
                      backgroundColor: "#F8FAFC",
                      height: "48px",
                      border: "1px solid #E2E8F0",
                      transition: "all 0.3s ease",
                      "&:hover": { borderColor: "#6D28D9" },
                    },
                    "& input": { color: "#1E3A8A", fontSize: "1rem" },
                  }}
                />
              </MDBox>

              {authError && (
                <MDTypography variant="caption" color="error" mb={2}>
                  {authError}
                </MDTypography>
              )}

              <MDButton
                type="submit"
                fullWidth
                sx={{
                  background: "linear-gradient(90deg, #3B82F6 0%, #8B5CF6 100%)",
                  color: "#FFFFFF", // Reforçado para garantir que o texto seja branco
                  borderRadius: "12px",
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: "medium",
                  fontSize: "1rem",
                  boxShadow: "0 4px 15px rgba(59, 130, 246, 0.3)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)",
                    boxShadow: "0 6px 20px rgba(59, 130, 246, 0.4)",
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    color: "#FFFFFF", // Garante que o texto permaneça branco mesmo quando desabilitado
                    opacity: 0.6,
                  },
                }}
                disabled={isLoading}
              >
                {isLoading ? "Carregando..." : "Entrar"}
              </MDButton>

              <MDBox mt={6}>
                <MDBox position="relative">
                  <MDBox sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center" }}>
                    <MDBox sx={{ width: "100%", borderTop: "1px solid", borderColor: "grey.200" }} />
                  </MDBox>
                  <MDBox sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
                    <MDTypography
                      variant="body2"
                      sx={{ px: 3, bgcolor: "rgba(255, 255, 255, 0.95)", color: "grey.500", fontSize: "0.9rem" }}
                    >
                      OU
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox mt={6}>
                  <MDButton
                    variant="outlined"
                    fullWidth
                    onClick={handleGoogleSignIn}
                    sx={{
                      borderRadius: "12px",
                      py: 1.5,
                      borderColor: "grey.300",
                      color: "#1E3A8A",
                      textTransform: "none",
                      fontWeight: "medium",
                      fontSize: "1rem",
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: "grey.50",
                        borderColor: "grey.400",
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                    disabled={isLoading}
                  >
                    <MDBox
                      component="img"
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google Icon"
                      sx={{ width: "24px", height: "24px", mr: 2 }}
                    />
                    Entrar com Google
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox textAlign="center" mt={6}>
                <MDTypography variant="body2" sx={{ color: "grey.600", fontSize: "0.9rem" }}>
                  Não tem uma conta?{" "}
                  <Link
                    to="/authentication/sign-up"
                    style={{
                      color: "#6D28D9",
                      fontWeight: "medium",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Cadastrar
                  </Link>
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>

      {/* Modal de Erro */}
      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 300,
            bgcolor: "background.paper",
            borderRadius: "10px",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
            p: 4,
            textAlign: "center",
          }}
        >
          <MDTypography variant="h6" mb={2} sx={{ color: "#1E3A8A" }}>
            {modalMessage || "Algo deu errado. Tente novamente."}
          </MDTypography>
          <MDButton
            variant="contained"
            sx={{
              background: "linear-gradient(90deg, #3B82F6 0%, #8B5CF6 100%)",
              color: "white",
              borderRadius: "8px",
              "&:hover": { background: "linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)" },
            }}
            onClick={() => {
              setShowModal(false);
              navigate("/authentication/sign-up");
            }}
          >
            Criar Conta
          </MDButton>
        </Box>
      </Modal>
    </MDBox>
  );
}

export default Login;
